import { Link } from "react-router-dom";
import type { PartyInviteResponse } from "@bw/shared/types/generated";
import { formatInviteExpiry } from "../lib/formatInviteExpiry";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { PartyInviteListProps } from "../partyInviteList.types";

function inviterLabel(invite: PartyInviteResponse): string {
  return invite.inviter_display_name?.trim() || "a player";
}

function InviterLine({ invite }: { invite: PartyInviteResponse }) {
  if (invite.inviter_display_name?.trim()) {
    return (
      <Typography>
        <Typography component="span" sx={{ fontWeight: 800 }}>
          {invite.inviter_display_name}
        </Typography>{" "}
        invited you
      </Typography>
    );
  }
  return <Typography>A player invited you</Typography>;
}

export function PartyInviteList(props: PartyInviteListProps) {
  return (
    <List disablePadding aria-label="Party invitations">
      {props.invites.map((invite) => {
        const identity = `${inviterLabel(invite)}, party ${invite.party_code}`;
        return (
          <ListItem
            key={invite.invite_id}
            divider
            sx={{
              px: 2, py: 1.5, alignItems: "flex-start", gap: 2,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Stack spacing={0.5} sx={{
              minWidth: 0, flex: 1, width: { xs: "100%", sm: "auto" }, overflowWrap: "anywhere",
            }}>
              <InviterLine invite={invite} />
              <Typography variant="body2">
                Party size: {invite.party_member_count}{" "}
                {invite.party_member_count === 1 ? "player" : "players"} in
                party
              </Typography>
              <Chip
                label={`Party code ${invite.party_code}`}
                size="small"
                variant="outlined"
                sx={{
                  alignSelf: "flex-start", height: "auto", maxWidth: "100%",
                  "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: 0.25 },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Respond by{" "}
                <time dateTime={invite.expires_at}>
                  {formatInviteExpiry(invite.expires_at)}
                </time>
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", maxWidth: "100%" }}>
              {props.mode === "respond" ? (
                <>
                  <Button
                    ref={
                      invite.invite_id === props.acceptButtonRefInviteId
                        ? props.acceptButtonRef
                        : undefined
                    }
                    type="button"
                    variant="contained"
                    size="small"
                    aria-label={`Accept invite from ${identity}`}
                    aria-disabled={props.actionsDisabled || undefined}
                    onClick={() => {
                      if (!props.actionsDisabled) {
                        props.onRespond(invite, true);
                      }
                    }}
                  >
                    Accept
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    aria-label={`Decline invite from ${identity}`}
                    aria-disabled={props.actionsDisabled || undefined}
                    onClick={() => {
                      if (!props.actionsDisabled) {
                        props.onRespond(invite, false);
                      }
                    }}
                  >
                    Decline
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    component={Link}
                    variant="outlined"
                    size="small"
                    aria-label={`View invitation from ${identity}`}
                    to={`/dropin/${invite.facility_id}/session`}
                  >
                    View invitation
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    aria-label={`Decline invite from ${identity}`}
                    disabled={props.declineDisabled || props.decliningInviteIds.has(invite.invite_id)}
                    onClick={() => {
                      if (!props.declineDisabled && !props.decliningInviteIds.has(invite.invite_id)) {
                        props.onDecline(invite);
                      }
                    }}
                  >
                    {props.decliningInviteIds.has(invite.invite_id) ? "Declining…" : "Decline"}
                  </Button>
                </>
              )}
            </Stack>
          </ListItem>
        );
      })}
    </List>
  );
}
